// src/pages/Check.js

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AssetUploader from '../components/AssetUploader';

import { readC2PA, retrieveManifest, compareManifests } from '../utils/c2paUtils';

import DMRC_LOGO from '../assets/powered-by-digimarc.svg';
import US_STATE from '../assets/us-state-guinea-logo.jpg';
import ExpandMoreIcon from '../assets/dropdown_icon.svg';
import DisplayResultPanel from '../components/DisplayResultPanel';
import DisplayContentCredentials from '../components/DisplayContentCredentials';

import { useTranslation } from 'react-i18next';


export default function CheckPage() {
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [c2paResult, setC2paResult] = useState(null);
  const [alertPanelData, setAlertPanelData] = useState(null);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [c2paSource, setC2paSource] = useState(null);
  const [showOriginalManifest, setShowOriginalManifest] = useState(false);
  const [originalManifest, setOriginalManifest] = useState(null);
  const [originalC2paSource, setOriginalC2paSource] = useState(null);
  const { t, i18n } = useTranslation();

  const DMRC_CERT_ID = '104451317974280210961599146181711920221';
  const CUSTOMER = 'U.S. Embassy Guinea';

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng); // Change the language
  };

  const handleSetFiles = (newFiles) => {
    setShowAlert(false);
    setFiles(newFiles);
  };

  const handleRemoveFile = (uploadId) => {
    const newFiles = files.filter((file) => file.uploadId !== uploadId);
    setFiles(newFiles);
    if (newFiles.length === 0) {
      setErrors([]);
      setShowAlert(false);
    }
    setC2paResult(null);
    setAlertPanelData(null);
    setOriginalUrl(null);
    setShowOriginalManifest(false);
  };

  const handleCancel = () => {
    setFiles([]);
    setShowOriginalManifest(false);
  };

  const readDropzoneFile = async (file) => {
    const { success, manifest, originalAssetUrl, pHash, expectedPHash } = await retrieveManifest(file);
    if (success) {
      return { success, manifest, originalAssetUrl, pHash, expectedPHash };
    }
    return { success: false };
  };

  const validateCustomer = (assertions, certValue, DMRC_CERT_ID, CUSTOMER) => {
    // Find the assertion with the label "stds.schema-org.CreativeWork"
    const creativeWorkAssertion = assertions.find(
      (assertion) => assertion.label === "stds.schema-org.CreativeWork"
    );

    // Extract the producer / author names
    const authorNames = creativeWorkAssertion?.data?.author?.map((author) => author.name) || [];

    // Validate the certificate and author name
    const isValid = certValue === DMRC_CERT_ID && authorNames[0] === CUSTOMER;

    return isValid;
  };

  const buildPanel = (manifest, retrievedManifest, isSimilar, pHash, expectedPHash) => {
    if (!manifest && !retrievedManifest)
      return {
        text: t('noManifestNoWatermark'),
        level: 'error',
        canDownloadOriginalImage: false,
      };

    if (manifest && !retrievedManifest)
      return {
        text: t('noManifestRetrieved'),
        level: 'warning',
        canDownloadOriginalImage: false,
      };

    if (!manifest && retrievedManifest) {
      if (pHash && expectedPHash && pHash === expectedPHash) {

        // This is used to assess that the content is from the customer
        // It checks that producer is the customer and the signature comes from Digimarc.
        // TODO: Change for actual customer cert check once we have a cert for the customer
        const activeManifestID = retrievedManifest.active_manifest;
        const activeManifest = retrievedManifest.manifests?.[activeManifestID];
        const certValue = activeManifest?.signature_info?.cert_serial_number;

        // Check if the certificate value and author name match the expected values
        const isValid = validateCustomer(
          activeManifest.assertions,
          certValue,
          DMRC_CERT_ID,
          CUSTOMER
        );

        if (isValid) {
          return {
            text: t('manifestRemovedCustomer'),
            level: 'info',
            canDownloadOriginalImage: true,
          };
        }
        else {
          return {
            text: t('manifestRemoved'),
            level: 'info',
            canDownloadOriginalImage: true,
          };
        }
      } else {
        return {
          text: t('modified'),
          level: 'error',
          canDownloadOriginalImage: true,
        };
      }
    }

    // TODO: check Phash here --> image visually modified

    // We have both manifests
    if (manifest && manifest.validationStatus && manifest.validationStatus.length) {
      return {
        // text: 'This image has been modified. The hard bindings are not matching.',
        text: t('modified'),
        level: 'error',
        canDownloadOriginalImage: true,
      };
    }

    if (isSimilar) {
      if (pHash && expectedPHash && pHash !== expectedPHash) {
        return {
          text: t('modified'),
          level: 'error',
          canDownloadOriginalImage: true,
        };
      }

      // This is used to assess that the content is from the customer
      // It checks that producer is the customer and the signature comes from Digimarc.
      const activeManifest = manifest.activeManifest;
      const certValue = activeManifest.signatureInfo?.cert_serial_number;

      // Check if the certificate value and author name match the expected values
      const isValid = validateCustomer(
        activeManifest.assertions?.data,
        certValue,
        DMRC_CERT_ID,
        CUSTOMER
      );

      if (isValid) {
        return {
          text: t('successCustomer'),
          level: 'success',
          canDownloadOriginalImage: false,
        };
      }
      else return {
        text: t('success'),
        level: 'success',
        canDownloadOriginalImage: false,
      }
    }

    return {
      text: t('noMatch'),
      level: 'error',
      canDownloadOriginalImage: true,
    };
  };

  const handleVerifyContentCredentials = async () => {
    if (files.length === 0) {
      setErrors([{ message: t('noImageSelected'), severity: 'error' }]);
      setShowAlert(true);
      return;
    }

    setUploading(true);
    setProgress({});

    if (files.length > 0) {
      const file = files[0];
      try {
        const res = await readC2PA(file.sourceUrl);
        const { success, manifest, originalAssetUrl, pHash, expectedPHash } = await readDropzoneFile(file);
        const isSimilar = await compareManifests(file, originalAssetUrl);

        console.log('phash', pHash);
        console.log('expectedPhash', expectedPHash);
        console.log('phash === expectedPHash', pHash === expectedPHash);

        setC2paSource(res?.source);
        setAlertPanelData(buildPanel(res?.manifest, manifest, isSimilar, pHash, expectedPHash));
        setOriginalUrl(originalAssetUrl);
        setC2paResult(success ? success : true);
        setUploading(false);

        setManifest(res?.manifest);
        if (success) {
          if (originalAssetUrl) {
            setShowOriginalManifest(true);
            const originalRes = await readC2PA(originalAssetUrl);
            setOriginalManifest(originalRes?.manifest);
            setOriginalC2paSource(originalRes?.source);
          }
        }
      } catch (error) {
        console.error('Error verifying content credentials:', error);
        setErrors([{ message: t('error'), severity: 'error' }]);
        setShowAlert(true);
        setUploading(false);
      }
    }
  };

  const handleCheckMoreImages = () => {
    setFiles([]);
    setC2paResult(null);
    setAlertPanelData(null);
    setOriginalUrl(null);
    setShowOriginalManifest(false);
  };

  const handleExpandMore = () => {
    setShowOriginalManifest(!showOriginalManifest);
  };

  return (
    <>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="left"
        alignItems="center"
        height="64px"
        padding="0 32px"
        backgroundColor="white"
      >
        <img
          src={US_STATE}
          alt="US Embassy Guinea Logo"
          style={{
            width: '80px', // Adjust the width as needed
            height: 'auto', // Maintain aspect ratio
            marginRight: '16px', // Add spacing between the new logo and the DMRC_LOGO
          }}
        />
        <img
          src={DMRC_LOGO}
          alt="logo"
          style={{
            width: '140px', // Adjust the width as needed
            height: 'auto', // Maintain aspect ratio
          }}
        />
      </Box>
      <Box style={{ height: '8px', background: 'rgb(71, 202, 209)', margin: '0' }}></Box>

      {/* Main Container */}
      <Box
        display={c2paResult ? 'flex' : 'block'}
        flexDirection={c2paResult ? 'row' : 'column'}
        width="100%"
      >
        {/* Main Content */}
        <Stack
          spacing={4}
          mb={3}
          backgroundColor="rgb(250, 250, 250)"
          flexGrow={1} // Allows the Stack to take up remaining space
          padding="0 32px"
        >

          {/* Language Selector */}
          <Box
            display="flex"
            justifyContent="left"
            sx={{
              paddingTop: '8px' // Space after the line
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '16px',
                borderRadius: '0',
                marginRight: '8px' // Add spacing between the buttons
              }}
              onClick={() => changeLanguage('en')}
            >English</Button>
            <Button
              variant="outlined"
              color="secondary"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '16px',
                borderRadius: '0',
                marginRight: '8px'
              }}
              onClick={() => changeLanguage('fr')}
            >Fran&ccedil;ais</Button>
          </Box>

          {/* Title */}
          <Box>
            <Typography
              style={{
                fontSize: '2.125rem',
                fontWeight: '700',
                letterSpacing: '-1.5px',
                fontFamily: 'Inter var, sans-serif',
                lineHeight: '1.167',
                color: 'rgb(29, 29, 29)'
              }}
            >
              {t('title')}
            </Typography>
          </Box>

          {/* Description */}
          <Box marginTop="32px">
            <Typography
              style={{
                fontSize: '1.2rem',
                fontWeight: '400',
                letterSpacing: '-0.5px',
                fontFamily: 'Inter, sans-serif',
                lineHeight: '1.41',
                color: '#757575',
              }}
            >
              {t('description')}
            </Typography>
          </Box>

          <Box marginTop="32px"> </Box>
          <Card elevation={0} variant="outlined">
            <CardContent
              sx={{
                padding: 3,
                '&:last-child': {
                  paddingBottom: 3,
                },
              }}
            >
              {/* Alert */}
              <Collapse in={showAlert}>
                <Alert
                  variant="outlined"
                  severity={errors.length > 0 ? errors[0].severity : 'error'}
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      aria-label="dismiss"
                      onClick={() => setShowAlert(false)}
                    >
                      {t('dismiss')}
                    </Button>
                  }
                  sx={{ mb: 3, '.MuiAlert-action': { pt: 0 } }}
                >
                  {errors.length > 0 && errors[0].message}
                </Alert>
              </Collapse>

              <Stack spacing={2}>
                {/* Asset Uploader */}
                <AssetUploader
                  files={files}
                  onSetFiles={handleSetFiles}
                  onRemoveFile={handleRemoveFile}
                  uploading={uploading}
                  progress={progress}
                  showCloseIcon={!uploading && !c2paResult}
                />

                {/* Display Result Panel */}
                {alertPanelData && <DisplayResultPanel alertPanelData={alertPanelData} />}

                {/* Mobile View: Content Credentials */}
                <Box
                  sx={{
                    display: {
                      xs: 'block', // Show on extra-small screens (mobile)
                      md: 'none', // Hide on small screens and up (600px and above)
                    },
                  }}
                >
                  {alertPanelData && alertPanelData.level === 'success' && manifest && (
                    <DisplayContentCredentials manifest={manifest} source={c2paSource} />
                  )}

                  {alertPanelData && alertPanelData.level === 'warning' && manifest && (
                    <DisplayContentCredentials manifest={manifest} source={c2paSource} />
                  )}
                </Box>

                {/* Error Case: Display Original Image */}
                {alertPanelData && (alertPanelData.level === 'error' || alertPanelData.level === 'info') && originalUrl && (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    {/* Centered Box */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      sx={{ mx: 'auto' }}
                    >
                      <Typography
                        style={{
                          fontSize: '0.8rem',
                          fontWeight: '700',
                          letterSpacing: '-0.5px',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: '1.41',
                          color: '#000000',
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                        }}
                      >
                        {t('originalImage')}
                      </Typography>
                      <Card
                        elevation={0}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          mb: 0,
                          borderRadius: 1,
                          borderColor: 'grey.300',
                          '&:last-child': {
                            pb: 0,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            color="#757575"
                            margin={0}
                          >
                            <Box
                              display="flex"
                              justifyContent="center" // Centers the container horizontally
                              width="100%"
                            >
                              <CardContent
                                sx={{
                                  p: 0,
                                  width: 'auto', // Adjusted from '100%' to 'auto'
                                  maxWidth: {
                                    sm: 500,
                                  },
                                  margin: '0 auto', // Centers the CardContent
                                  pb: '0 !important', // Removes the padding-bottom
                                }}
                              >
                                <img
                                  src={originalUrl}
                                  alt="preview"
                                  style={{
                                    width: '100%',
                                    maxWidth: '500px', // Optional: Limit the maximum width
                                    objectFit: 'cover',
                                    display: 'block', // Ensures proper margin auto behavior
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                  }}
                                />
                              </CardContent>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>

                    {/* Expand/Collapse Button for Original Manifest */}
                    <Box
                      sx={{
                        display: {
                          xs: 'block', // Show on extra-small screens (mobile)
                          md: 'none', // Hide on small screens and up (600px and above)
                        },
                      }}
                    >
                      {originalManifest && (
                        <>
                          <Button
                            color="secondary"
                            onClick={handleExpandMore}
                            style={{
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '16px',
                              borderRadius: '0',
                              width: '100%',
                            }}
                          >
                            <img
                              src={ExpandMoreIcon}
                              alt="Expand"
                              style={{
                                width: '60px',
                                aspectRatio: 'auto 124 / 12',
                                height: 'auto',
                                marginRight: '8px',
                                transition: 'transform 0.3s ease-in-out',
                                transform: showOriginalManifest ? 'rotate(180deg)' : 'rotate(0deg)',
                                cursor: 'pointer',
                              }}
                            />
                          </Button>

                          <Collapse in={showOriginalManifest}>
                            <DisplayContentCredentials manifest={originalManifest} source={c2paSource} />
                          </Collapse>
                        </>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Action Buttons: Check and Cancel */}
                {!c2paResult && (
                  <Stack
                    direction={{ xs: 'column', md: 'row' }} // Column on mobile, row on desktop
                    spacing={2}
                    sx={{
                      alignItems: { xs: 'center', md: 'flex-start' }, // Center on mobile, left on desktop
                      width: { xs: '100%', md: 'auto' }, // Full width on mobile, auto on desktop
                      mb: 2,
                    }}
                  >
                    <LoadingButton
                      variant="contained"
                      loading={uploading}
                      onClick={handleVerifyContentCredentials}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '16px',
                        borderRadius: '0',
                        width: { xs: '100%', md: 'auto' }, // Full width on mobile, auto on desktop
                      }}
                    >
                      {t('verify')}
                    </LoadingButton>

                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={uploading}
                      onClick={handleCancel}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '16px',
                        borderRadius: '0',
                        width: { xs: '100%', md: 'auto' }, // Full width on mobile, auto on desktop
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Button to Check Another Asset */}
          {c2paResult && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCheckMoreImages}
              sx={{
                width: { xs: '100%', md: 'fit-content' },
                marginTop: 2,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '16px',
              }}
            >
              {t('checkAnother')}
            </Button>
          )}
        </Stack>

        {/* Side Panel */}
        {c2paResult && (
          <Box
            sx={{
              display: {
                xs: 'none', // Hide on extra-small screens (mobile)
                md: 'flex', // Show on medium screens and up
              },
              padding: '24px',
              maxWidth: '450px', // Ensures the side panel doesn't exceed 400px
              flexShrink: 0, // Prevents the side panel from shrinking
              marginLeft: 'auto', // Pushes the side panel to the right
              marginTop: '16px',
            }}
          >
            {alertPanelData && alertPanelData.level === 'success' && manifest && (
              <DisplayContentCredentials manifest={manifest} source={c2paSource} />
            )}

            {alertPanelData && (alertPanelData.level === 'error') && originalUrl && (
              <Box display="flex" flexDirection="row" alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      letterSpacing: '-0.5px',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.41',
                      color: '#000000',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    {t('originalCC')}
                  </Typography>

                  {originalManifest && (
                    <DisplayContentCredentials manifest={originalManifest} source={c2paSource} />
                  )}
                </Box>
              </Box>
            )}

            {/* Content Credentials recovered */}
            {alertPanelData && alertPanelData.level === 'info' && originalUrl && (
              <Box display="flex" flexDirection="row" alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      letterSpacing: '-0.5px',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.41',
                      color: '#000000',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    {t('recoveredCC')}
                  </Typography>

                  {originalManifest && (
                    <DisplayContentCredentials manifest={originalManifest} source={c2paSource} />
                  )}
                </Box>
              </Box>
            )}

            {alertPanelData && (alertPanelData.level === 'warning') && manifest && (
              <DisplayContentCredentials manifest={manifest} source={c2paSource} />
            )}
          </Box>
        )}
      </Box>
    </>
  );
};